import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { CatalogContentBundle, CatalogEditDraft, CatalogEntry, CatalogKind } from '@/types/catalog.ts';
import { useCreateCatalogDraft } from '@/hooks/useCatalogEditDraft.ts';
import SectorForm from './forms/SectorForm.tsx';
import SubSectorForm from './forms/SubSectorForm.tsx';
import SimpleBriefingForm from './forms/SimpleBriefingForm.tsx';
import SimulationTypeForm from './forms/SimulationTypeForm.tsx';

interface Props {
  isOpen: boolean;
  kind: CatalogKind;
  entry: CatalogEntry | null;
  onClose: () => void;
  onSaved: (draft: CatalogEditDraft) => void;
}

const SUPPORTED_LANGS = ['es', 'it'] as const;

function emptyBundle(): CatalogContentBundle {
  return { slug: '', parentSlug: null, label: '', briefing: '' };
}

function bundleFromEntry(entry: CatalogEntry, lang: string): CatalogContentBundle {
  return {
    slug: entry.slug,
    parentSlug: entry.parentSlug ?? null,
    label: entry.labels[lang] ?? entry.labels.es ?? '',
    briefing: entry.briefings[lang] ?? entry.briefings.es ?? '',
  };
}

export default function CatalogEntryFormModal({ isOpen, kind, entry, onClose, onSaved }: Props) {
  const { t, i18n } = useTranslation();
  const initialLang = (i18n.resolvedLanguage ?? 'es').toLowerCase();
  const [language, setLanguage] = useState<string>(
    SUPPORTED_LANGS.includes(initialLang as 'es' | 'it') ? initialLang : 'es',
  );
  const [content, setContent] = useState<CatalogContentBundle>(emptyBundle);
  const [error, setError] = useState<string | null>(null);
  const { create, loading } = useCreateCatalogDraft();

  const mode: 'create' | 'update' = entry ? 'update' : 'create';

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    const lang = SUPPORTED_LANGS.includes(initialLang as 'es' | 'it') ? initialLang : 'es';
    setLanguage(lang);
    setContent(entry ? bundleFromEntry(entry, lang) : emptyBundle());
  }, [isOpen, entry, initialLang]);

  useEffect(() => {
    if (!isOpen || !entry) return;
    setContent(bundleFromEntry(entry, language));
  }, [language, entry, isOpen]);

  const baseline = useMemo<CatalogContentBundle>(
    () => (entry ? bundleFromEntry(entry, language) : emptyBundle()),
    [entry, language],
  );

  const isDirty = useMemo(() => {
    const norm = (s?: string | null) => (s ?? '').trim();
    if (mode === 'create') {
      return Boolean(
        norm(content.label) || norm(content.slug) || norm(content.briefing) || norm(content.parentSlug),
      );
    }
    return (
      norm(content.label) !== norm(baseline.label) ||
      norm(content.briefing) !== norm(baseline.briefing) ||
      norm(content.parentSlug) !== norm(baseline.parentSlug)
    );
  }, [mode, content, baseline]);

  const formNode = useMemo(() => {
    switch (kind) {
      case 'Sector':
        return <SectorForm value={content} onChange={setContent} mode={mode} />;
      case 'SubSector':
        return <SubSectorForm value={content} onChange={setContent} mode={mode} />;
      case 'SalePhase':
      case 'PriceRange':
        return <SimpleBriefingForm value={content} onChange={setContent} mode={mode} />;
      case 'Difficulty':
        return <SimpleBriefingForm value={content} onChange={setContent} mode={mode} slugEditable={false} />;
      case 'SimulationType':
        return <SimulationTypeForm value={content} onChange={setContent} mode={mode} />;
      default:
        return null;
    }
  }, [kind, content, mode]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!content.label.trim()) {
      setError(t('superuser.catalog.form.labelRequired', { defaultValue: 'Etiqueta requerida.' }));
      return;
    }
    if (mode === 'create' && !content.slug?.trim()) {
      setError(t('superuser.catalog.form.slugRequired', { defaultValue: 'Slug requerido.' }));
      return;
    }
    if (mode === 'create' && content.slug && !/^[a-z0-9-]+$/.test(content.slug)) {
      setError(t('superuser.catalog.form.slugInvalid', { defaultValue: 'Slug solo lowercase + guiones.' }));
      return;
    }
    if (kind === 'SubSector' && !content.parentSlug) {
      setError(t('superuser.catalog.form.parentRequired', { defaultValue: 'Sector padre requerido.' }));
      return;
    }
    if (!content.briefing?.trim()) {
      setError(t('superuser.catalog.form.briefingRequired', { defaultValue: 'Briefing requerido.' }));
      return;
    }

    const payload = {
      entryId: entry?.id ?? null,
      operation: mode === 'create' ? ('Create' as const) : ('Update' as const),
      language,
      content: {
        ...content,
        slug: mode === 'create' ? content.slug?.trim() : entry?.slug ?? content.slug,
        parentSlug: kind === 'SubSector' ? content.parentSlug : null,
        briefing: content.briefing ?? null,
      },
    };

    try {
      const draft = await create(kind, payload);
      toast.success(t('superuser.catalog.toast.draftSaved', {
        defaultValue: 'Cambio guardado, traducción en curso. Te notificaremos.',
      }));
      onSaved(draft);
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'ERROR';
      const localized = t(`superuser.catalog.errors.${raw}`, { defaultValue: raw });
      setError(localized);
    }
  }

  const title = entry
    ? t('superuser.catalog.form.editTitle', { defaultValue: 'Editar entrada' })
    : t('superuser.catalog.form.createTitle', { defaultValue: 'Nueva entrada' });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {t('superuser.catalog.form.description', {
              kind,
              defaultValue: 'Tipo: {{kind}}. Edita en un idioma; el resto se traduce automáticamente.',
            })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              {t('superuser.catalog.form.editingIn', { defaultValue: 'Editando en idioma:' })}
            </label>
            <div className="inline-flex rounded-lg border border-border bg-card p-1">
              {SUPPORTED_LANGS.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setLanguage(lang)}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium uppercase transition-colors ${
                    language === lang ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>
          </div>

          {formNode}

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              {t('common.cancel', { defaultValue: 'Cancelar' })}
            </Button>
            <Button
              type="submit"
              disabled={loading || !isDirty}
              title={!isDirty ? t('superuser.catalog.form.noChanges', { defaultValue: 'Sin cambios respecto al estado actual.' }) : undefined}
            >
              {loading && <Loader2 className="size-4 animate-spin" />}
              {t('superuser.catalog.form.save', { defaultValue: 'Guardar' })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
